import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Typography,
  useTheme,
} from "@mui/material";
import { DateTime } from "luxon";

import { SettingPopover, UtfDateRangePicker } from "@utilifeed/components";
import { SolidCard } from "@components";

import { Resolution, RESOLUTION_OPTIONS } from "./ControlPanel";
import QuickOptionButtonGroup from "./QuickOptionButtonGroup";
import type { DateRange, GraphSettings } from "./types";

type QuickDateOption = {
  label: string;
  value: string;
  getDates?: () => DateRange;
};

type GraphWithControlsProps = {
  // Required props
  title: string;
  children: React.ReactNode;

  // Control panel configuration
  showControls?: boolean;
  showQuickDates?: boolean;
  showDataSettings?: boolean;

  // Date range
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;

  // Resolution
  resolution?: Resolution;
  onResolutionChange?: (resolution: Resolution) => void;
  hiddenResolutions?: Resolution[];

  // Display options
  displayOptions?: Array<{
    key: string;
    label: string;
    checked: boolean;
  }>;
  onDisplayOptionsChange?: (key: string, checked: boolean) => void;

  // Graph settings (for backwards compatibility)
  graphSettings?: GraphSettings;
  onGraphSettingsChange?: (settings: GraphSettings) => void;

  // Additional customization
  settingsPopoverContent?: React.ReactNode;
  controlsAddon?: React.ReactNode;
  minDate?: DateTime;
  maxDate?: DateTime;
};

export const GraphWithControls: React.FC<GraphWithControlsProps> = ({
  title,
  children,
  showControls = true,
  showQuickDates = true,
  showDataSettings = true,
  dateRange,
  onDateRangeChange,
  resolution = "hourly",
  onResolutionChange,
  hiddenResolutions = [],
  displayOptions = [],
  onDisplayOptionsChange,
  graphSettings,
  onGraphSettingsChange,
  settingsPopoverContent,
  controlsAddon,
  minDate,
  maxDate = DateTime.now(),
}) => {
  const { t } = useTranslation(["_action", "_common"]);
  const theme = useTheme();

  const DEFAULT_QUICK_DATE_OPTIONS: QuickDateOption[] = [
    {
      label: t("text_last_day"),
      value: "lastDay",
      getDates: () => ({
        startDate: DateTime.now().minus({ days: 1 }),
        endDate: DateTime.now(),
      }),
    },
    {
      label: t("text_last_week"),
      value: "lastWeek",
      getDates: () => ({
        startDate: DateTime.now().minus({ weeks: 1 }),
        endDate: DateTime.now(),
      }),
    },
    {
      label: t("text_last_month"),
      value: "lastMonth",
      getDates: () => ({
        startDate: DateTime.now().minus({ months: 1 }),
        endDate: DateTime.now(),
      }),
    },
  ];

  const quickDateOptions = DEFAULT_QUICK_DATE_OPTIONS;

  // State for quick date selection
  const [selectedQuickDate, setSelectedQuickDate] = useState<string>("lastWeek");
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLButtonElement | null>(null);

  // Local state for popover controls
  const [localDateRange, setLocalDateRange] = useState<DateRange>(dateRange);
  const [localResolution, setLocalResolution] = useState<Resolution>(resolution);
  const [localDisplayOptions, setLocalDisplayOptions] = useState(
    displayOptions.reduce(
      (acc, opt) => ({ ...acc, [opt.key]: opt.checked }),
      {} as Record<string, boolean>
    )
  );
  const [localGraphSettings, setLocalGraphSettings] = useState(graphSettings);

  // Reset local state when popover opens
  const handleOpenSettings = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setPopoverAnchorEl(event.currentTarget);
      setLocalDateRange(dateRange);
      setLocalResolution(resolution);
      setLocalDisplayOptions(
        displayOptions.reduce(
          (acc, opt) => ({ ...acc, [opt.key]: opt.checked }),
          {} as Record<string, boolean>
        )
      );
      setLocalGraphSettings(graphSettings);
    },
    [dateRange, resolution, displayOptions, graphSettings]
  );

  const handleCloseSettings = () => {
    setPopoverAnchorEl(null);
  };

  // Confirm handler: apply local state to parent state
  const handleConfirmSettings = useCallback(() => {
    onDateRangeChange(localDateRange);

    if (onResolutionChange) {
      onResolutionChange(localResolution);
    }

    if (onDisplayOptionsChange) {
      Object.entries(localDisplayOptions).forEach(([key, checked]) => {
        const currentOption = displayOptions.find((opt) => opt.key === key);
        if (currentOption && currentOption.checked !== checked) {
          onDisplayOptionsChange(key, checked);
        }
      });
    }

    if (onGraphSettingsChange && localGraphSettings) {
      onGraphSettingsChange(localGraphSettings);
    }

    handleCloseSettings();
  }, [
    localDateRange,
    localResolution,
    localDisplayOptions,
    localGraphSettings,
    onDateRangeChange,
    onResolutionChange,
    onDisplayOptionsChange,
    onGraphSettingsChange,
    displayOptions,
  ]);

  const handleCancelSettings = useCallback(() => {
    setPopoverAnchorEl(null);
    setLocalDateRange(dateRange);
    setLocalResolution(resolution);
    setLocalDisplayOptions(
      displayOptions.reduce(
        (acc, opt) => ({ ...acc, [opt.key]: opt.checked }),
        {} as Record<string, boolean>
      )
    );
    setLocalGraphSettings(graphSettings);
  }, [dateRange, resolution, displayOptions, graphSettings]);

  const handleQuickDateSelect = (option: QuickDateOption) => {
    setSelectedQuickDate(option.value);
    const newDateRange = option.getDates();
    onDateRangeChange(newDateRange);
  };

  const handleLocalDateRangeChange = (start: DateTime | null, end: DateTime | null) => {
    setLocalDateRange({ startDate: start, endDate: end });
    setSelectedQuickDate("custom");
  };

  // Controls component
  const renderControls = () => {
    if (!showControls) return null;

    return (
      <Box display="flex" gap={2}>
        {showQuickDates && (
          <QuickOptionButtonGroup
            options={quickDateOptions}
            activeValue={selectedQuickDate}
            onChange={handleQuickDateSelect}
            ariaLabel="Time range quick options"
          />
        )}

        {showDataSettings && (
          <Button
            onClick={handleOpenSettings}
            fullWidth
            size="small"
            variant="outlined"
            sx={{ borderRadius: "16px", px: 2, py: 0.75, textTransform: "none" }}
          >
            {t("text_data_settings", { ns: "_common" })}
          </Button>
        )}

        {controlsAddon}
      </Box>
    );
  };

  // Available resolutions (filtered)
  const availableResolutions = Object.values(RESOLUTION_OPTIONS).filter(
    (option) => !hiddenResolutions.includes(option.value as Resolution)
  );

  return (
    <>
      <SolidCard title={title} titleAddon={renderControls()} variant="cardLayout">
        {children}
      </SolidCard>

      {/* Data Settings Popover */}
      {showDataSettings && (
        <SettingPopover
          id="data-setting"
          open={!!popoverAnchorEl}
          anchorEl={popoverAnchorEl}
          handleClose={handleCancelSettings}
        >
          {settingsPopoverContent || (
            <Paper variant="outlined" sx={{ p: 2, width: "360px" }}>
              {/* Header */}
              <Box display="flex" alignContent="center" sx={{ mb: 2 }}>
                <Box display="flex" flexGrow={1} alignSelf="center">
                  <Typography variant="labelLarge">
                    {t("text_data_settings", { ns: "_common" })}
                  </Typography>
                </Box>
                <IconButton
                  disableRipple
                  disableFocusRipple
                  sx={{ padding: 0 }}
                  onClick={handleCancelSettings}
                >
                  <HighlightOffIcon color="primary" />
                </IconButton>
              </Box>

              {/* Content */}
              <Box sx={{ backgroundColor: theme.palette.background.paper, p: 2 }}>
                {/* Display Options */}
                {displayOptions.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="labelLarge">
                      {t("text_display_options", { ns: "_common" })}
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", px: 1 }}>
                      {displayOptions.map((option) => (
                        <Box
                          key={option.key}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="bodyMedium">{option.label}</Typography>
                          <Checkbox
                            size="small"
                            color="primary"
                            checked={localDisplayOptions[option.key] || false}
                            onChange={(e) =>
                              setLocalDisplayOptions({
                                ...localDisplayOptions,
                                [option.key]: e.target.checked,
                              })
                            }
                            disableRipple
                          />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Graph Settings (backwards compatibility) */}
                {graphSettings && onGraphSettingsChange && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="labelLarge">
                      {t("text_graph_settings", { ns: "_common" })}
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", px: 1 }}>
                      {Object.entries(graphSettings).map(([key, value]) => (
                        <Box
                          key={key}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="bodyMedium">
                            {t(`text_${key}`, { ns: "_common" })}
                          </Typography>
                          <Checkbox
                            size="small"
                            color="primary"
                            checked={localGraphSettings?.[key] || false}
                            onChange={(e) =>
                              setLocalGraphSettings({
                                ...localGraphSettings,
                                [key]: e.target.checked,
                              })
                            }
                            disableRipple
                          />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Date Selection */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="labelLarge" sx={{ display: "block", mb: 1 }}>
                    {t("text_date_selection", { ns: "_common" })}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                    <UtfDateRangePicker
                      startDate={localDateRange.startDate}
                      endDate={localDateRange.endDate}
                      minDate={minDate}
                      maxDate={maxDate}
                      onChange={handleLocalDateRangeChange}
                      size="small"
                      data-testid="date-range-picker"
                      inputVariant="outlined"
                    />
                  </Box>
                </Box>

                {/* Resolution */}
                {onResolutionChange && availableResolutions.length > 0 && (
                  <Box>
                    <Typography variant="labelLarge" sx={{ display: "block", mb: 1 }}>
                      {t("text_resolution", { ns: "_common" })}
                    </Typography>
                    <Select
                      value={localResolution}
                      onChange={(event) => setLocalResolution(event.target.value as Resolution)}
                      fullWidth
                      size="small"
                      sx={{
                        minWidth: 120,
                        "& .MuiSelect-select": {
                          py: 1,
                        },
                      }}
                    >
                      {availableResolutions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {t(option.label, { ns: "_common" })}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                )}

                {/* Footer */}
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  columnGap={1}
                  mt={2}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    sx={{ borderRadius: "16px", px: 2, py: 0.75, textTransform: "none" }}
                    onClick={handleConfirmSettings}
                  >
                    {t("action_confirm", { ns: "_action" })}
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    sx={{ borderRadius: "16px", px: 2, py: 0.75, textTransform: "none" }}
                    onClick={handleCancelSettings}
                  >
                    {t("action_cancel", { ns: "_action" })}
                  </Button>
                </Box>
              </Box>
            </Paper>
          )}
        </SettingPopover>
      )}
    </>
  );
};
